import * as React from 'react';
import styled, { css } from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { Button, TextButton } from '@creditornot/cb-button';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Popover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { ReactRouterMenuLinkItem } from 'common/components/cookbook/ReactRouterMenuLinkItem';
import { Stack } from 'common/components/cookbook/Stack';
import { useMatchMedia } from 'common/hooks/useMatchMedia';
import { media } from 'common/media';

import { DashboardEmbedState } from './DashboardEmbedState';
import { useDashboardsQuery } from './query';
import { useDashboardRoute } from './useDashboardRoute';

const StyledNav = styled.nav<{ $isLoading: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 320px;
  padding: 12px 24px 24px;
  height: 100%;
  border-right: 1px solid ${color.border};

  @media ${media.md} {
    width: 100%;
  }

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      align-items: center;
      padding-top: 32px;
    `}
`;

// does not require localization
export const BoardSetNavigation = () => {
  const { data, isLoading, error, isSuccess } = useDashboardsQuery();
  const { dashboardPageTitle, dashboardPage } = useDashboardRoute();
  const md = useMatchMedia('md');
  const [open, setOpen] = React.useState(false);

  if (error) {
    return null;
  }

  return (
    <Popover
      offset={4}
      placement="bottom-start"
      renderArrow={false}
      onOpenChange={setOpen}
      open={open}
      content={({ close }) => (
        <StyledNav $isLoading={isLoading}>
          {isLoading && <Spinner size="small" />}
          {!isLoading &&
            data &&
            data.map(({ id, title }) => (
              <ReactRouterMenuLinkItem
                data-test-id={title}
                key={title}
                active={dashboardPage?.id === id}
                to="/analytics/$dashboardId"
                params={{ dashboardId: id }}
                onClick={close}
              >
                {title}
              </ReactRouterMenuLinkItem>
            ))}
          <Button size="small" variant="secondary" onClick={() => setOpen(false)}>
            Close
          </Button>
        </StyledNav>
      )}
      zIndex={10}
    >
      {({ refProps }) => (
        <Stack direction="row" gap="regularXxxlarge" {...refProps}>
          <TextButton
            variant="brand"
            size="small"
            icon={icons.chevronDown}
            iconPosition="end"
            data-test-id="select-country-button"
            {...refProps}
          >
            {dashboardPageTitle}
          </TextButton>
          {md && isSuccess && <DashboardEmbedState />}
        </Stack>
      )}
    </Popover>
  );
};
